import { computed, ref, useId, type ComputedRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNxToasts, type NxErrorSummaryEntry } from '@blpde/nx-ui';
import {
  errorSummaryBindings,
  originKey,
  reportWriteFailure,
  useErrorSummary,
  useFieldErrors,
  type FieldErrors,
  type ProblemFieldError,
} from '../../http';

/**
 * A dictionary side panel's refused write (I-2813): field errors on the
 * inputs, everything else in the panel's error summary, the draft left as
 * typed. A second submit while one is in flight, or while a write may have
 * landed, does nothing.
 */
export function useDictionaryEditorWrite(options: {
  kind: () => 'create' | 'update';
  fields: readonly string[];
  labelFor?: (field: ProblemFieldError) => string | undefined;
  reload: () => void;
}): {
  entries: ComputedRef<NxErrorSummaryEntry[]>;
  onReload: (id: string) => void;
  onCopyFailed: (requestId: string) => void;
  focusRequests: Readonly<Ref<number>>;
  fieldErrors: FieldErrors;
  locked: ComputedRef<boolean>;
  saving: ComputedRef<boolean>;
  run: (write: () => Promise<void>) => Promise<void>;
} {
  const { t } = useI18n();
  const { showToast } = useNxToasts();
  const summary = useErrorSummary();
  const { entries, onReload, onCopyFailed } = errorSummaryBindings(summary, { showToast, t });
  const fieldErrors = useFieldErrors(options.fields);
  const saving = ref(false);
  const origin = originKey(useId(), 'action', 'save');
  const locked = computed(() => saving.value || summary.locked.value);

  async function run(write: () => Promise<void>): Promise<void> {
    if (locked.value) return;
    const attempt = summary.begin(origin);
    saving.value = true;
    fieldErrors.clearAll();
    try {
      await write();
    } catch (cause) {
      reportWriteFailure(cause, {
        origin,
        attempt,
        t,
        kind: options.kind(),
        closesDialog: true,
        fields: fieldErrors,
        context: summary,
        reload: options.reload,
        focus: true,
        labelFor: options.labelFor,
      });
    } finally {
      saving.value = false;
    }
  }

  return {
    entries,
    onReload,
    onCopyFailed,
    focusRequests: summary.focusRequests,
    fieldErrors,
    locked,
    saving: computed(() => saving.value),
    run,
  };
}
